import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type SignaturePadProps = {
  label: string;
  disabled?: boolean;
  onChange: (file: File | null) => void;
};

export function SignaturePad({ label, disabled, onChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    // Ink stays near-black. The PNG is stored as drawn and does not follow the app theme.
    ctx.strokeStyle = '#111827';
  }, []);

  function pointerPos(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function startDraw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) {
      return;
    }
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) {
      return;
    }
    drawing.current = true;
    canvasRef.current?.setPointerCapture(event.pointerId);
    const { x, y } = pointerPos(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function draw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || disabled) {
      return;
    }
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) {
      return;
    }
    const { x, y } = pointerPos(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  }

  function endDraw(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) {
      return;
    }
    drawing.current = false;
    canvasRef.current?.releasePointerCapture(event.pointerId);
    void exportPng(true);
  }

  function clear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  }

  async function exportPng(ink = hasInk) {
    const canvas = canvasRef.current;
    if (!canvas || !ink) {
      onChange(null);
      return;
    }
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/png'),
    );
    if (!blob) {
      onChange(null);
      return;
    }
    onChange(new File([blob], 'signature.png', { type: 'image/png' }));
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={label}
        className={cn(
          // Paper stays white so the stored signature matches the light sheet.
          'h-40 w-full touch-none rounded-md border bg-white',
          disabled && 'opacity-60',
        )}
        onPointerDown={startDraw}
        onPointerMove={draw}
        onPointerUp={endDraw}
        onPointerLeave={endDraw}
      />
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          disabled={disabled}
          onClick={clear}
        >
          Löschen
        </Button>
      </div>
    </div>
  );
}
